import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useAuthenticatedFirebaseUser } from '@/auth/useFirebaseUser'
import { SupportPanel, SupportPanelSheet } from '@/components/organisms/SupportPanel'
import { type SupportPageOverride, SupportPageOverrideContext } from '@/hooks/useSupportContext'
import { useSupportPanelStore } from '@/state/support/supportPanelStore'
import { agentOpenSupportRequestsQuery } from '@/state/support/supportRequests'
import { useSupportAgentUnreadCount } from '@/state/support/useAgentSupportRequests'
import { useRequesterSupportRequests } from '@/state/support/useRequesterSupportRequests'
import { useSupportReads } from '@/state/support/useSupportReads'
import { useSupportRequesterSound } from '@/state/support/useSupportRequesterSound'

import { RequesterSupportPanel } from '../RequesterSupportPanel'

import { isPiloteUrl, postToPilote, usePiloteBadge, usePiloteFrame } from './piloteFrame'

export interface SupportEmbedSearch {
  readonly page?: string
  readonly object?: string
  readonly objectUrl?: string
}

const usePiloteCloseNotice = (open: boolean) => {
  const wasOpen = useRef(open)
  useEffect(() => {
    if (wasOpen.current && !open) {
      postToPilote({ type: 'soutien:close' })
    }
    wasOpen.current = open
  }, [open])
}

const EmbeddedRequesterSupport = ({ pageOverride }: { readonly pageOverride: SupportPageOverride }) => {
  const { open, view, openPanel, closePanel } = useSupportPanelStore()
  const { requests, loadState, retry } = useRequesterSupportRequests()
  const { isUnreadByRequester } = useSupportReads()
  useSupportRequesterSound(requests, { isLoaded: loadState === 'ready', panelOpen: open })
  usePiloteBadge(requests.filter((request) => isUnreadByRequester(request)).length)
  usePiloteFrame(openPanel)
  usePiloteCloseNotice(open)

  return (
    <SupportPageOverrideContext value={pageOverride}>
      <SupportPanelSheet
        embedded
        open={open}
        wide={view.kind !== 'requests'}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            openPanel()
          } else {
            closePanel()
          }
        }}>
        <RequesterSupportPanel requests={requests} loadState={loadState} onRetry={retry} />
      </SupportPanelSheet>
    </SupportPageOverrideContext>
  )
}

const EmbeddedAgentSupport = () => {
  const { data } = useQuery(agentOpenSupportRequestsQuery())
  usePiloteBadge(useSupportAgentUnreadCount(data?.data ?? []))
  usePiloteFrame()
  return null
}

export const SupportEmbedPage = ({ page, object, objectUrl }: SupportEmbedSearch) => {
  const { supportRequester, supportRole } = useAuthenticatedFirebaseUser()
  const pageOverride = useMemo<SupportPageOverride>(
    () => ({
      page: page ?? '',
      object: object && objectUrl && isPiloteUrl(objectUrl) ? { label: object, url: objectUrl } : null,
    }),
    [page, object, objectUrl]
  )

  if (supportRole !== null) {
    return <EmbeddedAgentSupport />
  }
  if (!supportRequester) {
    return null
  }
  return <EmbeddedRequesterSupport pageOverride={pageOverride} />
}

export const SupportEmbedUnavailable = () => {
  const [open, setOpen] = useState(false)
  const openPanel = useCallback(() => {
    setOpen(true)
  }, [])
  usePiloteFrame(openPanel)
  usePiloteCloseNotice(open)

  return (
    <SupportPanelSheet embedded open={open} wide={false} onOpenChange={setOpen}>
      <SupportPanel
        view="unavailable"
        onRetry={() => {
          globalThis.location.reload()
        }}
        onClose={() => {
          setOpen(false)
        }}
      />
    </SupportPanelSheet>
  )
}
